import {
  AtomicFilter,
  CollatorType,
  FilterOperator,
  JsonObjectSearchFilterVisitor,
  MARKETPLACE_PROJECTS_CONSUMABLE,
} from '@agilelab/plugin-wb-search-common';
import { useApi } from '@mainstage/core-plugin-api';
import { JsonObject } from '@mainstage/types';
import useAsync from 'react-use/lib/useAsync';
import { witboostSearchApiRef } from '../api';
import { resolveAvailableValues } from '../filterResolvers';
import {
  FacetedValues,
  FilterResolversOverride,
  SearchFiltersContext,
} from '../types';
import { useSearchFilterConfig } from './useSearchFilterConfig';
import { ConsumableMode } from '@agilelab/plugin-wb-marketplace-common';
import { useFacetedValues } from './useFacetedValues';
import { useMemo, useRef } from 'react';
import { AsyncState } from 'react-use/lib/useAsyncFn';

const DefaultFiltersOverride = {};

/**
 * Stabilize an AsyncState reference: useAsync returns a new object on every
 * render even when loading/error/value haven't changed. This hook keeps the
 * previous reference when the meaningful fields are identical, preventing
 * downstream useMemo / context consumers from seeing a new object each cycle.
 */
function useStableAsync<T>(state: AsyncState<T>): AsyncState<T> {
  const ref = useRef(state);
  if (
    ref.current.loading !== state.loading ||
    ref.current.error !== state.error ||
    ref.current.value !== state.value
  ) {
    ref.current = state;
  }
  return ref.current;
}

export const useSearchFilters = (
  type: CollatorType,
  filterResolversOverride: FilterResolversOverride = DefaultFiltersOverride,
  currentFilters?: JsonObject,
  queryTerm?: string,
  taxonomyRef?: string,
): SearchFiltersContext => {
  const { filtersConfig, filtersConfigReady } = useSearchFilterConfig(
    type,
    taxonomyRef,
  );
  const searchApi = useApi(witboostSearchApiRef);

  const consumableFilterJsonObject = new AtomicFilter(
    MARKETPLACE_PROJECTS_CONSUMABLE.field,
    FilterOperator.IN,
    [ConsumableMode.Consumable, ConsumableMode.HasConsumableChild],
  ).accept(new JsonObjectSearchFilterVisitor());

  // TODO: put preFilter as optional input param
  const preFilters: JsonObject =
    type === CollatorType.MARKETPLACE_PROJECTS
      ? consumableFilterJsonObject
      : undefined;

  // We exclude values related to not consumable entities
  const rawAvailableValues = useAsync(async () => {
    if (!filtersConfigReady) {
      return new Promise<never>(() => {});
    }
    return resolveAvailableValues(
      type,
      filtersConfig,
      searchApi,
      filterResolversOverride,
      preFilters,
    );
  }, [type, filtersConfig, filterResolversOverride, filtersConfigReady]);
  const availableValues = useStableAsync(rawAvailableValues);

  const rawFacetedValues = useAsync(async () => {
    if (!filtersConfigReady) {
      return new Promise<never>(() => {});
    }

    const raw = await searchApi.getAvailableFilterValues(type, {
      currentFilters,
      queryTerm,
    });

    const res: FacetedValues = {};

    // transform into an object where the value of each filter is mapped to its count for convenience
    Object.entries(raw).forEach(([label, counts]) => {
      res[label] = counts.reduce<Record<string, number>>(
        (acc, { value, count }) => {
          acc[value] = count ? parseInt(count, 10) : 0;
          return acc;
        },
        {},
      );
    });

    return res;
  }, [type, currentFilters, queryTerm, filtersConfigReady]);
  const facetedValues = useStableAsync(rawFacetedValues);

  const enrichedValues = useFacetedValues(
    availableValues.value,
    facetedValues.value,
    filtersConfig,
  );

  const result = useMemo(
    () => ({
      config: filtersConfig,
      availableValues,
      facetedValues,
      enrichedValues,
    }),
    [filtersConfig, availableValues, facetedValues, enrichedValues],
  );

  return result;
};
